import moment from 'moment-timezone';
import React, { useContext, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useRecoilValueLoadable } from 'recoil';
import { getRecoil, setRecoil } from 'recoil-nexus';
import styled from 'styled-components';
import InterphoneConfirmModal from '../../../components/modal/InterphoneConfirmModal';
import HotelController, { AreaType, RoomType } from '../../../controller/HotelController';
import InterphoneController, {
  CALL_TYPE_SENDER,
  MissedCallType,
} from '../../../controller/InterphoneController';
import { CurrentStoreContext } from '../../../controller/StoreController';
import { AuthContext } from '../../../navigation/AuthProvider';
import { INTERPHONE_STATUS_SENDER_WAIT, SENDER_TYPE_HOTEL } from '../../../recoil/area';
import { fcmInterphoneState } from '../../../recoil/fcmMessage';
import { hotelAreaIdState } from '../../../recoil/interphone';
import toast from 'react-hot-toast';

const InterphoneSetting = (oProps: any) => {
  const { currentStore } = useOutletContext<CurrentStoreContext>();
  const oAuthContext = useContext(AuthContext);
  const user_id = oAuthContext.userInfo.user_id;

  const [missedCallList, setMissedCallList] = useState<MissedCallType[]>([]);
  const [deviceList, setDeviceList] = useState<AreaType[]>([]);
  const [isOpen, setIsOpen] = useState<boolean[]>([]);

  const [selectedRoom, setSelectedRoom] = useState<RoomType | MissedCallType | null>(null);
  const [visibleConfirm, setVisibleConfirm] = useState<boolean>(false);

  const hotelAreaId = useRecoilValueLoadable<number | null>(hotelAreaIdState);

  // API
  const getDeviceList = async () => {
    try {
      const oParams = {
        store_id: currentStore.value,
        user_id: user_id,
      };
      const oRes: any = await HotelController.getDevice(oParams);

      if (oRes && oRes.data) {
        const oResponse: any = oRes.data.data;
        setDeviceList(oResponse);
        setIsOpen(Array.from({ length: oResponse.length }, (_, i) => true));
      }
    } catch (err) {
      console.log('err-->', err);
    }
  };

  // 부재중 전화 목록 조회
  const getUnavailableCalls = async () => {
    try {
      const oParams = {
        receiver: SENDER_TYPE_HOTEL,
        receiver_id: getRecoil(hotelAreaIdState),
      };

      const response = await InterphoneController.getUnavailableCalls(oParams);

      if (response && response.data) {
        const oRes = response.data;
        console.log('getUnavailableCalls', oRes);
        if (oRes.data && oRes.data.unavailableCalls) {
          setMissedCallList(oRes.data.unavailableCalls);
        }
      }
    } catch (err) {
      console.log('getUnavailableCalls err-->', err);
    }
  };

  // 통화 요청
  const requestVoiceChat = async () => {
    try {
      if (selectedRoom) {
        const hotelAreaId = getRecoil(hotelAreaIdState);
        let oParams;
        if ('area_id' in selectedRoom) {
          oParams = {
            room_area_id: selectedRoom.area_id,
            hotel_area_id: hotelAreaId,
            sender: SENDER_TYPE_HOTEL,
          };
        } else if ('sender_id' in selectedRoom) {
          oParams = {
            room_area_id: selectedRoom.sender_id,
            hotel_area_id: hotelAreaId,
            sender: 'HOTEL_AREA',
          };
        }

        if (oParams) {
          const response = await InterphoneController.requestVoiceChat(oParams);

          if (response && response.data) {
            const oRes = response.data;
            console.log('requestVoiceChat', oRes);
            if (oRes.data && oRes.data.false_reason === null) {
              setRecoil(fcmInterphoneState, {
                ...getRecoil(fcmInterphoneState),
                type: CALL_TYPE_SENDER,
                interphone_call_manage_id: oRes.data.interphone_call_manage_id,
                room_name: oRes.data.room_name,
                status: INTERPHONE_STATUS_SENDER_WAIT,
              });
              onCloseConfirmModal();
            } else {
              toast.error('현재 해당 구역은 통화중이에요');
            }
          }
        }
      }
    } catch (err) {
      console.log('requestVoiceChat err-->', err);
    }
  };

  // 부재중 삭제
  const checkUnavailableCall = async () => {
    try {
      if (selectedRoom && 'sender_id' in selectedRoom) {
        const oParams = {
          manage_id: selectedRoom.manage_id,
        };

        const response = await InterphoneController.checkUnavailableCall(oParams);

        if (response && response.data) {
          const oRes = response.data;
          console.log('checkUnavailableCall', oRes);
          if (oRes.data && oRes.data.result) {
            setMissedCallList((prev) =>
              prev.filter((item) => item.manage_id !== selectedRoom.manage_id),
            );
            onCloseConfirmModal();
          }
        }
      }
    } catch (err) {
      console.log('checkUnavailableCall err-->', err);
    }
  };

  // 아이템 박스 여닫기
  const openHeaderBox = (index: number) => {
    let isOpenTemp = [...isOpen];
    isOpenTemp[index] = !isOpenTemp[index];
    setIsOpen(isOpenTemp);
  };

  // device status를 확인하여 객실의 상태값 받기
  const getDeviceStatus = (room: RoomType) => {
    const alltime = room.device_list.filter((item) => item.device_status === 'alltime').length;
    const checkin = room.device_list.filter((item) => item.device_status === 'checkint').length;
    const checkout = room.device_list.filter((item) => item.device_status === 'checkout').length;

    const counts: Record<'alltime' | 'checkin' | 'checkout', number> = {
      alltime,
      checkin,
      checkout,
    };

    return (Object.keys(counts) as (keyof typeof counts)[]).reduce((a, b) =>
      counts[a] >= counts[b] ? a : b,
    );
  };

  const getMissedCallStatus = (call: MissedCallType) => {
    const filteredRoom = deviceList
      .flatMap((area) => area.room)
      .find((room) => room.area_id === call.sender_id);
    if (filteredRoom) {
      return getDeviceStatus(filteredRoom);
    }
  };

  const getSelectedRoomName = () => {
    if (selectedRoom) {
      if ('area_id' in selectedRoom) {
        return selectedRoom.name;
      } else {
        return selectedRoom.sender_name;
      }
    } else return '';
  };

  const getCalledAtText = (time: string) => {
    return moment(time).format('M월 D일 H:m');
  };

  // confirmModal
  const openInterphoneConfirm = (item: RoomType | MissedCallType) => {
    setSelectedRoom(item);
    setVisibleConfirm(true);
    console.log('item', item);
  };

  const onConfirmConfirmModal = () => {
    requestVoiceChat();
  };

  const onCancelConfirmModal = () => {
    checkUnavailableCall();
  };

  const onCloseConfirmModal = () => {
    setVisibleConfirm(false);
    setSelectedRoom(null);
  };

  useEffect(() => {
    getDeviceList();
  }, []);

  useEffect(() => {
    console.log('hotelAreaId', hotelAreaId);
    if (hotelAreaId.state === 'hasValue' && hotelAreaId.contents) {
      getUnavailableCalls();
    }
  }, [hotelAreaId]);

  return (
    <>
      <Style>
        <div className="contentContainer">
          {missedCallList.length > 0 && (
            <div className="listItem missed">
              <div className={`head`}>
                <img
                  src={`${process.env.PUBLIC_URL}/svg/ic_warning_white.svg`}
                  alt="warning_white"
                />
                <h4>부재중 전화</h4>
              </div>
              <div className="content">
                {missedCallList.map((call, index) => (
                  <button
                    key={index}
                    className="contentItem"
                    onClick={() => openInterphoneConfirm(call)}>
                    <div className="statusBox">
                      <p
                        className={`
                                ${getMissedCallStatus(call) === 'checkin' && 'checkin'}`}>
                        {
                          {
                            alltime: '상시',
                            checkin: '입실',
                            checkout: '공실',
                          }[getMissedCallStatus(call)!]
                        }
                      </p>
                    </div>
                    <div className="row">
                      <h4>{call.sender_name}</h4>
                      <img
                        className="arrow"
                        src={`${process.env.PUBLIC_URL}/svg/ic_arrow_right.svg`}
                        alt="right"
                      />
                    </div>
                    <p>{getCalledAtText(call.called_at)}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
          <h3>통화할 객실 선택</h3>
          <div className="listWrap">
            {deviceList.map((area, index) => (
              <div key={index} className="listItem">
                <div className={`head`}>
                  <h4>{area.area_name}</h4>
                  <button onClick={() => openHeaderBox(index)}>
                    <img
                      src={`${process.env.PUBLIC_URL}/svg/ic_arrow_${
                        isOpen[index] ? 'up' : 'down'
                      }_grey.svg`}
                      alt="up"
                    />
                  </button>
                </div>
                {isOpen[index] && (
                  <div className="content">
                    {area.room.map((room, roomIdx) => (
                      <button
                        key={`${index}_${roomIdx}`}
                        className="contentItem"
                        onClick={() => openInterphoneConfirm(room)}>
                        {room.device_list.filter((item) => item.device_status !== 'abnormal')
                          .length > 0 && (
                          <div className="statusBox">
                            <p
                              className={`
                                ${getDeviceStatus(room) === 'checkin' && 'checkin'}`}>
                              {
                                {
                                  alltime: '상시',
                                  checkin: '입실',
                                  checkout: '공실',
                                }[getDeviceStatus(room)]
                              }
                            </p>
                          </div>
                        )}
                        <div className="row">
                          <h4>{room.name}</h4>
                          <img
                            className="arrow"
                            src={`${process.env.PUBLIC_URL}/svg/ic_arrow_right.svg`}
                            alt="right"
                          />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </Style>
      {selectedRoom && (
        <InterphoneConfirmModal
          isOpen={visibleConfirm}
          modalType={'room_area_id' in selectedRoom ? 'all' : 'confirm'}
          icon={`${process.env.PUBLIC_URL}/svg/ic_phone.svg`}
          iconClass="call"
          title={getSelectedRoomName()}
          description={'전화를 걸까요?'}
          onConfirmText={'전화걸기'}
          onCancelText={'목록 삭제하기'}
          onConfirm={onConfirmConfirmModal}
          onCancel={onCancelConfirmModal}
          onClose={onCloseConfirmModal}
        />
      )}
    </>
  );
};

export default InterphoneSetting;

const Style = styled.div`
  h3 {
    font-size: 18px;
    font-weight: 500;
    color: #555;
  }
  h4 {
    font-size: 16px;
    font-weight: 500;
  }

  .listWrap {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 20px 0;
  }

  .listItem {
    border-radius: 5px;
    border: 1px solid #e5e5e5;
    .head {
      position: relative;
      border-radius: 5px 5px 0 0;
      background: #f7f7f7;
      padding: 12px 20px;
      display: flex;
      justify-content: center;
      h4 {
        color: #555;
      }
      button {
        position: absolute;
        right: 20px;
        top: 50%;
        transform: translateY(-50%);
      }
    }
    .head.close {
      border-radius: 5px;
    }
    .content {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      padding: 10px;
      border-top: 1px solid #e5e5e5;
      border-radius: 0 0 5px 5px;

      .contentItem {
        width: calc((100% - 24px) / 5);
        height: 90px;
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        border-radius: 5px;
        border: 1px solid #e5e5e5;
        gap: 4px;
        h4 {
          margin-left: 8px;
          color: #555;
        }
        img {
          width: 16px;
          height: 16px;
        }
        .statusBox {
          position: absolute;
          top: 0;
          right: 0;
          border-radius: 0 5px 0 5px;
          border: 1px solid #e5e5e5;
          margin-top: -1px;
          margin-right: -1px;
          padding: 3px 10px;
          p {
            font-size: 10px;
            line-height: 14px;
            font-weight: 500;
            color: #979797;
          }
          p.checkin {
            color: #4dd2ae;
          }
        }
      }
    }
  }

  .listItem.missed {
    border: none;
    background: #ffeeee;
    margin-bottom: 60px;

    .head {
      border-radius: 5px 5px 0 0;
      background: #ff6060;
      padding: 13px 21px;
      justify-content: flex-start;
      gap: 6px;

      h4 {
        color: #fff;
      }
    }
    .content {
      gap: 0;
      padding: 20px 10px;
      border-top: none;
      border-radius: 0 0 5px 5px;
      gap: 6px 0;

      p {
        font-size: 10px;
        color: #a7a7a7;
        font-weight: 400;
      }

      .contentItem {
        width: calc((100%) / 5);
        height: 80px;
        border-radius: 0;
        border: none;
        border-left: 2px solid #fff;
        .row {
          margin-top: 20px;
        }

        .statusBox {
          border: none;
          margin: 0;
        }
      }
      .contentItem:nth-child(5n + 1) {
        border-left: none;
      }
    }
  }

  @media (max-width: 900px) {
    h4 {
      font-size: 12px;
      font-weight: 500;
    }
    .listItem {
      border-radius: 4px;
      .head {
        border-radius: 4px 4px 0 0;
        padding: 8px 16px;
        h4 {
          color: #555;
        }
        button {
          position: absolute;
          right: 20px;
          top: 50%;
          transform: translateY(-50%);
        }
      }
      .head.close {
        border-radius: 5px;
      }
      .content {
        gap: 4px;
        padding: 8px;
        border-radius: 0 0 4px 4px;

        .contentItem {
          width: calc((100% - 16px) / 5);
          height: 75px;
          border-radius: 4px;
          h4 {
            margin-left: 0;
          }
          .statusBox {
            border: none;
            border-radius: 0 4px 0 4px;
            padding: 2px 6px;
            p {
              font-size: 8px;
              line-height: 12px;
            }
          }
          .arrow {
            display: none;
          }
        }
      }
    }

    .listItem.missed {
      margin-bottom: 40px;

      .head {
        padding: 7px 13px;
        h4 {
          font-size: 14px;
        }
      }
      .content {
        padding: 14px 8px;
        .contentItem {
          width: calc((100%) / 4);
          height: 75px;
          padding: 0 10px;
          .row {
            margin-top: 12px;
          }
          p {
            line-height: 12px;
          }
        }
        .contentItem:nth-child(5n + 1) {
          border-left: 2px solid #fff;
        }
        .contentItem:nth-child(4n + 1) {
          border-left: none;
        }
      }
    }
  }
`;
